import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["sidebar"]
  openSidebarBound = null
  reapplyHighlightBound = null

  connect() {
    this.openSidebarBound = this.openSidebar.bind(this)
    document.addEventListener("turbo:frame-load", this.openSidebarBound)

    // Re-apply selection highlight after turbo stream renders replace DOM elements (Bug 3 fix)
    this.reapplyHighlightBound = this.reapplyHighlight.bind(this)
    document.addEventListener("turbo:before-stream-render", this.reapplyHighlightBound)

    // Check URL on initial load
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('selected')) {
      this.open()
      this.highlightSelected()
    }
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.openSidebarBound)
    document.removeEventListener("turbo:before-stream-render", this.reapplyHighlightBound)
  }

  // Bug 1 fix: Intercept clicks to bypass turbo frame navigation.
  // Links nested inside turbo frames get intercepted by Turbo's FrameController,
  // which conflicts with turbo_stream processing. We preventDefault, make a manual
  // fetch with the turbo_stream Accept header, and process the response directly.
  open(event) {
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.remove("hidden")

      if (event?.type === "click") {
        event.preventDefault()

        const link = event.currentTarget
        const url = new URL(link.href)
        const selected = url.searchParams.get('selected')

        if (selected) {
          // Update browser URL
          const currentUrl = new URL(window.location)
          currentUrl.searchParams.set('selected', selected)
          history.pushState({}, '', currentUrl)

          // Highlight the clicked row immediately
          this.highlightSelected(selected)

          // Manual fetch bypassing turbo frame navigation
          fetch(link.href, {
            headers: {
              'Accept': 'text/vnd.turbo-stream.html',
              'X-Requested-With': 'XMLHttpRequest'
            }
          })
          .then(response => response.text())
          .then(html => {
            Turbo.renderStreamMessage(html)
          })
        }
      }
    }
  }

  close() {
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.add("hidden")
      const url = new URL(window.location)
      url.searchParams.delete('selected')
      history.pushState({}, '', url)

      // Remove all selection highlights
      this.highlightSelected(null)
    }
  }

  openSidebar(event) {
    if (event.target.id === "sidebar_content") {
      this.open()
    }
  }

  toggle() {
    if (this.hasSidebarTarget) {
      if (this.sidebarTarget.classList.contains("hidden")) {
        this.open()
      } else {
        this.close()
      }
    }
  }

  // Bug 3 fix: After turbo stream renders replace DOM elements (e.g. after editing),
  // the selection highlight is lost. Wrap the stream render to reapply highlights.
  reapplyHighlight(event) {
    const originalRender = event.detail.render
    event.detail.render = (streamElement) => {
      originalRender(streamElement)
      this.highlightSelected()
    }
  }

  // Apply or remove CSS highlight on the selected OKR row.
  // Reads from URL params if no explicit value provided.
  highlightSelected(selected) {
    if (selected === undefined) {
      const urlParams = new URLSearchParams(window.location.search)
      selected = urlParams.get('selected')
    }

    const highlightClasses = ['bg-primary/10', 'border-2', 'border-primary', 'shadow-md']

    // Remove existing highlights from all OKR rows
    document.querySelectorAll('.okr-row').forEach(row => {
      row.classList.remove(...highlightClasses, 'bg-primary/5')
    })

    if (!selected) return

    // Find the selection link matching the selected param and highlight its parent row
    document.querySelectorAll('.okr-row a[data-action*="sidebar#open"]').forEach(link => {
      if (link.href && link.href.includes(`selected=${selected}`)) {
        const row = link.closest('.okr-row')
        if (row) {
          row.classList.add(...highlightClasses)
        }
      }
    })
  }
}
